// libraries
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { Table, TableProps, Tag, Tooltip } from 'antd';
import { SorterResult } from 'antd/lib/table/interface';
import {
	CommentOutlined,
	MessageOutlined,
	ProfileOutlined,
	CheckOutlined,
} from '@ant-design/icons';

// components
import { SharedTable } from '../../../shared/table';
import { SubscriptionTags, renderSubscription } from '../../../shared/subscriptions';

// utils
import { txt } from '../../../../locales/i18n';
import { formatDate } from '../../../../utils/date';

export function UserListContentDesktop(props: any): JSX.Element {
	const [showSubscriptionColumn, setShowSubscriptionColumn] = useState(false);
	const [sortedInfo, setSortedInfo] = useState<SorterResult<any>>({});

	const history = useHistory();
	const location = useLocation();

	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const subscription = searchParams.get('subscription');

		const showSubscriptionColumn = subscription !== null;
		setShowSubscriptionColumn(showSubscriptionColumn);
		if (showSubscriptionColumn === false) return;

		setSortedInfo({
			order: 'descend',
			field: 'currentSubscription',
		});
	}, [location.search]);

	function renderUniverse(value: string, record: any): JSX.Element | null {
		if (value === null) return null;

		if (record.universes.length > 1) {
			const remainingUniverse: number = record.universes.length - 1;

			return (
				<>
					<Tag color="blue">{value.toUpperCase()}</Tag>
					<span>+{remainingUniverse}</span>
				</>
			);
		}

		return <Tag color="blue">{value.toUpperCase()}</Tag>;
	}

	function renderNumbers(value: number): number | null {
		return value === 0 ? null : value;
	}

	function renderCheckmark(value: any): null | JSX.Element {
		if (Boolean(value) === false) return null;
		return <CheckOutlined />;
	}

	function renderOrigin(value: string): string | null {
		if (value === 'Boblberg') return null;
		return value;
	}

	function renderGender(gender: number) {
		if (gender === 1) return txt('genders.male');
		if (gender === 2) return txt('genders.female');
		if (gender === 3) return txt('genders.other');
		return null;
	}

	function handleOnRow(record: any): any {
		return {
			onClick: () => {
				history.replace({ hash: String(record.id), search: location.search });
				history.push(`/users/${record.id}`);
			},
		};
	}

	function handleSort(key: string) {
		function compare(a: any, b: any): -1 | 0 | 1 {
			let valueA: any = a[key];
			let valueB: any = b[key];

			if (Number.isNaN(valueA) && Number.isNaN(valueB)) {
				valueA = valueA.toLowerCase();
				valueB = valueB.toLowerCase();
			}

			if (valueA < valueB) return -1;
			if (valueA > valueB) return 1;
			return 0;
		}

		return { compare };
	}

	function handleSortSubscriptions(key: string) {
		function compare(a: any, b: any): -1 | 0 | 1 {
			let valueA: any = a[key]?.firstPeriodStart ?? a[key]?.created ?? '';
			let valueB: any = b[key]?.firstPeriodStart ?? b[key]?.created ?? '';

			if (Number.isNaN(valueA) && Number.isNaN(valueB)) {
				valueA = valueA.toLowerCase();
				valueB = valueB.toLowerCase();
			}

			if (valueA < valueB) return -1;
			if (valueA > valueB) return 1;
			return 0;
		}

		return { compare };
	}

	function getClassName(record: Record<string, any>): string {
		const classNames: string[] = [];

		if (Boolean(record.banned) === true) {
			classNames.push('red-row-highlight');
		}

		return classNames.join(' ');
	}

	const handleTableChange: TableProps<any>['onChange'] = (pagination, filters, sorter) => {
		setSortedInfo(sorter as SorterResult<any>);
	};

	return (
		<>
			{showSubscriptionColumn && <SubscriptionTags />}

			<SharedTable
				rowKey="id"
				loading={props.loading}
				dataSource={props.dataSource}
				onRow={handleOnRow}
				rowClassName={getClassName}
				onChange={handleTableChange}
			>
				<Table.Column
					dataIndex="created"
					title={txt('global.created')}
					render={formatDate}
					width="14em"
				/>

				<Table.Column dataIndex="id" title="ID" width="8em" responsive={['sm']} />

				<Table.Column
					dataIndex="username"
					title={txt('global.username')}
					width="12em"
					ellipsis
				/>

				<Table.Column dataIndex="email" title="Email" width="12em" ellipsis />

				<Table.Column
					dataIndex="age"
					title={txt('global.age')}
					width="6em"
					responsive={['sm']}
					sorter={handleSort('age')}
					showSorterTooltip={false}
					sortDirections={['descend', 'ascend']}
					sortOrder={sortedInfo.field === 'age' ? sortedInfo.order : null}
				/>

				<Table.Column
					dataIndex="municipality"
					title={txt('global.municipality')}
					width="12em"
					sorter={handleSort('municipality')}
					sortOrder={sortedInfo.field === 'municipality' ? sortedInfo.order : null}
					showSorterTooltip={false}
					sortDirections={['descend', 'ascend']}
				/>

				{showSubscriptionColumn && (
					<Table.Column
						dataIndex="currentSubscription"
						title={txt('global.subscription')}
						width="24ch"
						render={renderSubscription}
						ellipsis
						sorter={handleSortSubscriptions('currentSubscription')}
						showSorterTooltip={false}
						sortOrder={
							sortedInfo.field === 'currentSubscription' ? sortedInfo.order : null
						}
						defaultSortOrder="descend"
						sortDirections={['descend', 'ascend']}
					/>
				)}

				<Table.Column
					dataIndex="universe"
					title={txt('global.latest universe')}
					render={renderUniverse}
					responsive={['sm']}
					width="16em"
				/>

				<Table.Column
					dataIndex="posts"
					title={
						<Tooltip title={txt('global.posts')}>
							<ProfileOutlined />
						</Tooltip>
					}
					width="6em"
					render={renderNumbers}
					responsive={['sm']}
					sorter={handleSort('posts')}
					showSorterTooltip={false}
					sortDirections={['descend', 'ascend']}
					sortOrder={sortedInfo.field === 'posts' ? sortedInfo.order : null}
				/>

				<Table.Column
					dataIndex="messages"
					title={
						<Tooltip title={txt('global.messages')}>
							<CommentOutlined />
						</Tooltip>
					}
					width="6em"
					render={renderNumbers}
					responsive={['sm']}
					sorter={handleSort('messages')}
					showSorterTooltip={false}
					sortDirections={['descend', 'ascend']}
					sortOrder={sortedInfo.field === 'messages' ? sortedInfo.order : null}
				/>

				<Table.Column
					dataIndex="conversations"
					title={
						<Tooltip title={txt('global.conversations')}>
							<MessageOutlined />
						</Tooltip>
					}
					width="6em"
					render={renderNumbers}
					responsive={['sm']}
					sorter={handleSort('conversations')}
					showSorterTooltip={false}
					sortDirections={['descend', 'ascend']}
					sortOrder={sortedInfo.field === 'conversations' ? sortedInfo.order : null}
				/>

				<Table.Column
					dataIndex="hasApp"
					title="App"
					width="8ch"
					align="center"
					render={renderCheckmark}
				/>

				{/* <Table.Column
					dataIndex="origin"
					title={txt('global.origin')}
					render={renderOrigin}
					width="18ch"
					ellipsis
				/> */}

				<Table.Column
					dataIndex="gender"
					title={txt('global.gender')}
					render={renderGender}
					width="12ch"
				/>
			</SharedTable>
		</>
	);
}
